import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { INVITE_PROFESSIONALS_TO_JOBS } from '~/actions';
import { clearSuccess } from '~/actions/success';
import { jobKeys, useInviteProfessionalsToJobsMutation } from '~/domain/Jobs';
import {
  factoryGetInvitableOnsiteInterviewsKey,
  IGetInvitableOnsiteInterviewsResult,
} from '~/domain/PermanentJobs';

interface IUseInviteToInterviewProps {
  officeId: string;
  postingId: string;
  professionalId: string;
}

export const useInviteToInterview = ({
  officeId,
  postingId,
  professionalId,
}: IUseInviteToInterviewProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const isLoading = inviteProfessionalsToJobsMutation.isPending;

  const getIsLoadingById = (id: string) => {
    return isLoading && !!inviteProfessionalsToJobsMutation.variables?.jobIds?.includes(id);
  };

  const handleInvite = (id: string) => {
    if (isLoading) {
      return;
    }

    inviteProfessionalsToJobsMutation.mutate(
      {
        jobIds: [id],
        professionalIds: [professionalId],
        officeId,
      },
      {
        onSuccess: (_data, vars) => {
          const invitedInterviewIds = new Set(vars.jobIds);

          vars.jobIds.forEach((jobId) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
          });

          queryClient.setQueryData<IGetInvitableOnsiteInterviewsResult>(
            factoryGetInvitableOnsiteInterviewsKey({ postingId, professionalId }),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                data: old.data.map((interview) =>
                  invitedInterviewIds.has(interview.onsiteInterviewId)
                    ? { ...interview, isInvited: true }
                    : interview,
                ),
              };
            },
          );
          dispatch(clearSuccess(INVITE_PROFESSIONALS_TO_JOBS));
        },
      },
    );
  };

  return {
    isLoading,
    getIsLoadingById,
    handleInvite,
  };
};
